import { Button, Group, Modal, Stack, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useRestartCrossCameraPipeline } from '@/queries/crossCameraQueries'
import { showToast } from '@/theme/notifications'
import { CrossCameraPipeline } from '@/types/crossCamera'

type CrossCameraPipelineRestartModalProps = {
  pipeline: CrossCameraPipeline | null
  onClose: () => void
}

export const CrossCameraPipelineRestartModal = ({
  pipeline,
  onClose
}: CrossCameraPipelineRestartModalProps) => {
  const intl = useIntl()
  const { mutateAsync: restartPipeline, isPending } =
    useRestartCrossCameraPipeline()

  const handleRestartClick = async () => {
    if (!pipeline) return

    try {
      await restartPipeline(pipeline.id)
      showToast(
        intl.formatMessage({ id: 'crossCamera.pipelines.restart.success' }),
        'success'
      )
      onClose()
    } catch (error) {
      showToast(
        extractErrorMessage(
          error,
          intl.formatMessage({ id: 'crossCamera.pipelines.restart.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      opened={pipeline !== null}
      title={<FormattedMessage id="crossCamera.pipelines.restart.title" />}
      onClose={onClose}
    >
      <Stack gap="md">
        <Text size="sm">
          <FormattedMessage id="crossCamera.pipelines.restart.body" />
        </Text>

        <Group justify="end">
          <Button variant="subtle" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button loading={isPending} onClick={() => void handleRestartClick()}>
            <FormattedMessage id="crossCamera.pipelines.actions.restart" />
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
